import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, Brush,
} from 'recharts'
import { infrastructureApi, type HostChartAnnotation, type InfraInstanceRow, type RangeKey } from '../api/client'
import {
  Badge, Button, Card, EmptyState, Field, LinkButton, PageHeader,
  SegmentedControl, Skeleton, SortableTh, Textarea,
} from '../components/ui'
import { useListState } from '../hooks/useListState'
import { sortRows } from '../lib/list-state'

const RANGES: { label: string; value: RangeKey }[] = [
  { label: '24h', value: '24h' },
  { label: '7d', value: '7d' },
  { label: '30d', value: '30d' },
]

const ANNOTATION_MAX = 500

function extractApiError(e: unknown): string | undefined {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error
}

function num(n: unknown): number | null {
  const v = typeof n === 'number' ? n : Number(n)
  return Number.isFinite(v) ? v : null
}

function fmtBytes(n: unknown): string {
  const v = num(n)
  if (v === null) return '-'
  if (v <= 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(v) / Math.log(k)))
  return `${(v / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
}

function fmtTRY(n: unknown): string {
  const v = num(n)
  if (v === null) return '-'
  return `₺${v.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`
}

function fmtPct(n: unknown, decimals = 1): string {
  const v = num(n)
  if (v === null) return '-'
  return `${v.toFixed(decimals)}%`
}

function fmtFixed(n: unknown, decimals = 1): string {
  const v = num(n)
  if (v === null) return '-'
  return v.toFixed(decimals)
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${accent ?? 'text-gray-900'}`}>{value}</p>
      {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
    </Card>
  )
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'running' ? 'success'
      : status === 'error' ? 'danger'
        : status === 'stopped' ? 'neutral'
          : 'warning'
  return <Badge tone={tone}>{status}</Badge>
}

type SortKey = 'cpu_percent' | 'mem_bytes' | 'storage_bytes' | 'net_30d_bytes' | 'uptime_percent' | 'cost_try_30d' | 'subdomain'

export default function InfrastructurePage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [range, setRange] = useState<RangeKey>('24h')
  // Every column here is a cost or a usage number, so descending is right for
  // all of them — no defaultDirFor needed.
  const { sortKey, sortDir, toggleSort } = useListState<SortKey>({ sortKey: 'cost_try_30d' })

  // Annotation popover state. When the user clicks the chart, we open a small
  // input pinned to the bucket nearest to the click. Submitting POSTs the note
  // and refreshes the overview query so the marker appears.
  const [draftAnnot, setDraftAnnot] = useState<{ ts: string; bucketLabel: string } | null>(null)
  const [draftBody, setDraftBody] = useState('')
  const [annotError, setAnnotError] = useState<string | null>(null)
  // Track the last bucket the cursor hovered over so onClick has a target even
  // when Recharts' click payload arrives empty (happens on edge clicks / when
  // the click lands on an existing ReferenceLine).
  const lastHoveredRow = useRef<{ t: string; ts: number } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['infra-overview', range],
    queryFn: () => infrastructureApi.overview(range),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const sortedRows = useMemo(
    () => (data ? sortRows(data.instances, sortKey, sortDir) : []),
    [data, sortKey, sortDir],
  )

  // Bucket host_series for longer ranges so the chart stays readable.
  // 24h: keep raw samples (~5 min cadence already coarse enough).
  // 7d: bucket by 1h → ~168 points; CPU = max in bucket (preserve spikes), RAM = avg.
  // 30d: bucket by 4h → ~180 points; same aggregation.
  const hostSeries = useMemo(() => {
    if (!data || data.host_series.length === 0) return []
    const bucketMs = range === '7d' ? 60 * 60 * 1000
      : range === '30d' ? 4 * 60 * 60 * 1000
      : 0

    type Row = { t: string; ts: number; cpu: number; mem_pct: number }
    const fmt = (d: Date) => range === '24h'
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
        d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

    if (bucketMs === 0) {
      return data.host_series.map<Row>((s) => {
        const d = new Date(s.captured_at)
        return {
          t: fmt(d),
          ts: d.getTime(),
          cpu: s.cpu_percent,
          mem_pct: s.mem_total_bytes ? (s.mem_used_bytes / s.mem_total_bytes) * 100 : 0,
        }
      })
    }

    const buckets = new Map<number, { cpuMax: number; memSum: number; memCount: number }>()
    for (const s of data.host_series) {
      const ts = new Date(s.captured_at).getTime()
      const bucketStart = Math.floor(ts / bucketMs) * bucketMs
      const memPct = s.mem_total_bytes ? (s.mem_used_bytes / s.mem_total_bytes) * 100 : 0
      const b = buckets.get(bucketStart)
      if (!b) {
        buckets.set(bucketStart, { cpuMax: s.cpu_percent, memSum: memPct, memCount: 1 })
      } else {
        if (s.cpu_percent > b.cpuMax) b.cpuMax = s.cpu_percent
        b.memSum += memPct
        b.memCount += 1
      }
    }
    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map<Row>(([ts, b]) => ({
        t: fmt(new Date(ts)),
        ts,
        cpu: b.cpuMax,
        mem_pct: b.memSum / b.memCount,
      }))
  }, [data, range])

  // Spread out X-axis ticks for longer ranges so labels don't pile up.
  const xAxisInterval = range === '7d' ? Math.floor(hostSeries.length / 8)
    : range === '30d' ? Math.floor(hostSeries.length / 8)
    : 'preserveEnd' as const

  // Map each annotation to the nearest bucket on the bucketed series so it
  // renders on the same X tick the user sees. Annotations whose ts falls
  // outside the visible range are dropped (defensive — backend already filters).
  const annotationsForChart = useMemo(() => {
    if (!data || hostSeries.length === 0) return [] as Array<{ a: HostChartAnnotation; t: string }>
    return (data.annotations ?? [])
      .map((a) => {
        const annTs = new Date(a.ts).getTime()
        let nearest = hostSeries[0]
        let nearestDiff = Math.abs(hostSeries[0].ts - annTs)
        for (const row of hostSeries) {
          const d = Math.abs(row.ts - annTs)
          if (d < nearestDiff) { nearest = row; nearestDiff = d }
        }
        return { a, t: nearest.t }
      })
  }, [data, hostSeries])

  const createAnnotation = useMutation({
    mutationFn: (v: { ts: string; body: string }) => infrastructureApi.createAnnotation(v.ts, v.body),
    onSuccess: () => {
      setDraftAnnot(null)
      setDraftBody('')
      setAnnotError(null)
      qc.invalidateQueries({ queryKey: ['infra-overview', range] })
    },
  })

  const deleteAnnotation = useMutation({
    mutationFn: (id: string) => infrastructureApi.deleteAnnotation(id),
    onSuccess: () => {
      setAnnotError(null)
      qc.invalidateQueries({ queryKey: ['infra-overview', range] })
    },
  })

  type RechartsState = { activeLabel?: string; activePayload?: Array<{ payload: { t: string; ts: number } }> }
  const trackHover = (s: unknown) => {
    const row = (s as RechartsState)?.activePayload?.[0]?.payload
    if (row) lastHoveredRow.current = row
  }
  const openDraftAt = (s: unknown) => {
    const row = (s as RechartsState)?.activePayload?.[0]?.payload ?? lastHoveredRow.current
    if (!row) return
    setDraftAnnot({ ts: new Date(row.ts).toISOString(), bucketLabel: row.t })
    setDraftBody('')
    setAnnotError(null)
  }

  const submitAnnotation = () => {
    if (!draftAnnot) return
    const body = draftBody.trim()
    if (!body) return
    createAnnotation.mutate(
      { ts: draftAnnot.ts, body },
      {
        onError: (e) =>
          setAnnotError(extractApiError(e) ?? t('infra.annotationSaveFailed', { defaultValue: 'Could not save the note.' })),
      },
    )
  }

  const removeAnnotation = (id: string) => {
    deleteAnnotation.mutate(id, {
      onError: (e) =>
        setAnnotError(extractApiError(e) ?? t('infra.annotationDeleteFailed', { defaultValue: 'Could not delete the note.' })),
    })
  }

  return (
    <div className="p-4 sm:p-8">
      <PageHeader
        title={t('infra.title')}
        description={t('infra.subtitle')}
        actions={
          <SegmentedControl
            value={range}
            onChange={setRange}
            options={RANGES}
            label={t('infra.rangeLabel', { defaultValue: 'Date range' })}
          />
        }
      />

      {/* Host summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          label={t('infra.hostCPU')}
          value={data ? fmtPct(data.host.cpu_percent) : '-'}
          sub={data ? t('infra.containerCount', { count: data.host.container_count }) : ''}
        />
        <StatCard
          label={t('infra.hostRAM')}
          value={data ? `${fmtBytes(data.host.mem_used)} / ${fmtBytes(data.host.mem_total)}` : '-'}
          sub={data && data.host.mem_total ? fmtPct((data.host.mem_used / data.host.mem_total) * 100) : ''}
        />
        <StatCard
          label={t('infra.hostDisk')}
          value={data ? `${fmtBytes(data.host.disk_used)} / ${fmtBytes(data.host.disk_total)}` : '-'}
          sub={data && data.host.disk_total ? fmtPct((data.host.disk_used / data.host.disk_total) * 100) : ''}
        />
        <StatCard
          label={t('infra.serverBudget')}
          value={data ? fmtTRY(data.rates?.cost_basis) : '-'}
          sub={data ? [
            `${num(data.rates?.server_cores) ?? '-'} core · ${num(data.rates?.server_ram_gb) ?? '-'} GB · ${num(data.rates?.server_disk_gb) ?? '-'} GB`,
            data.rates?.includes_vat ? t('infra.netOfVat', { gross: fmtTRY(data.rates?.server_budget) }) : null,
          ].filter(Boolean).join(' · ') : ''}
        />
      </div>

      {/* Allocation vs capacity */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label={t('infra.allocatedCores')}
            value={`${fmtFixed(data.aggregate.total_allocated_cores, 1)} / ${num(data.aggregate.server_total_cores) ?? '-'}`}
            sub={t('infra.allocatedCoresHint')}
            accent={(num(data.aggregate.total_allocated_cores) ?? 0) > (num(data.aggregate.server_total_cores) ?? Infinity) ? 'text-red-600' : 'text-gray-900'}
          />
          <StatCard
            label={t('infra.allocatedRAM')}
            value={`${fmtFixed(data.aggregate.total_allocated_ram_gb, 1)} / ${num(data.aggregate.server_total_ram_gb) ?? '-'} GB`}
            sub={t('infra.allocatedRAMHint')}
            accent={(num(data.aggregate.total_allocated_ram_gb) ?? 0) > (num(data.aggregate.server_total_ram_gb) ?? Infinity) ? 'text-red-600' : 'text-gray-900'}
          />
          <StatCard
            label={t('infra.totalStorage')}
            value={fmtBytes(data.aggregate.total_storage_bytes)}
            sub={t('infra.instancesRunning', { running: data.aggregate.running_instances, total: data.aggregate.total_instances })}
          />
          <StatCard
            label={t('infra.backupStorage')}
            value={fmtBytes(data.aggregate.total_backup_bytes)}
            sub={t('infra.backupCount', { count: data.aggregate.total_backup_count })}
          />
        </div>
      )}

      {/* Overcommitment: reserved tenant RAM/CPU vs the physical box */}
      {data && (() => {
        const a = data.aggregate
        const bars = [
          { key: 'ram', label: t('infra.ocRAM'), pct: num(a.ram_overcommit_pct) ?? 0, detail: `${fmtFixed(a.total_allocated_ram_gb, 1)} / ${fmtFixed(a.server_total_ram_gb, 0)} GB` },
          { key: 'cpu', label: t('infra.ocCPU'), pct: num(a.cores_overcommit_pct) ?? 0, detail: `${fmtFixed(a.total_allocated_cores, 1)} / ${fmtFixed(a.server_total_cores, 0)} core` },
          { key: 'disk', label: t('infra.ocDisk'), pct: num(a.disk_used_pct) ?? 0, detail: `${fmtBytes(a.total_storage_bytes)} / ${fmtFixed(a.server_total_disk_gb, 0)} GB` },
        ]
        const oversold = (num(a.ram_overcommit_pct) ?? 0) > 100 || (num(a.cores_overcommit_pct) ?? 0) > 100
        return (
          <Card className="mb-6">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-700">{t('infra.overcommitTitle')}</h2>
              <span className={`text-xs font-medium ${oversold ? 'text-red-600' : 'text-green-700'}`}>
                {oversold ? t('infra.overcommitOversold') : t('infra.overcommitHeadroom', { count: a.instances_headroom })}
              </span>
            </div>
            <div className="space-y-3">
              {bars.map((b) => {
                const over = b.pct > 100
                const width = Math.min(100, b.pct)
                const color = b.pct > 100 ? 'bg-red-500' : b.pct > 85 ? 'bg-amber-500' : 'bg-brand-500'
                return (
                  <div key={b.key}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-gray-600">{b.label}</span>
                      <span className={over ? 'text-red-600 font-medium' : 'text-gray-500'}>{b.detail} · {fmtPct(b.pct, 0)}</span>
                    </div>
                    <div className="relative h-2.5 rounded-full bg-gray-100 overflow-hidden">
                      <div className={`h-full rounded-full ${color}`} style={{ width: `${width}%` }} />
                      {over && <div className="absolute inset-y-0 right-0 w-0.5 bg-red-700" />}
                    </div>
                  </div>
                )
              })}
            </div>
            <p className="text-xs text-gray-400 mt-3">{t('infra.overcommitNote', { gb: fmtFixed(a.typical_instance_ram_gb, 0) })}</p>
          </Card>
        )
      })()}

      {/* Host time series */}
      <Card className="mb-6">
        <div className="flex items-baseline justify-between mb-1">
          <h2 className="text-sm font-medium text-gray-700">{t('infra.hostTrend')}</h2>
          {(range === '7d' || range === '30d') && (
            <span className="text-xs text-gray-400">
              {t('infra.hostTrendBucket', { bucket: range === '7d' ? '1h' : '4h' })}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-400 mb-3">{t('infra.annotationHint')}</p>
        {hostSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={hostSeries}
              margin={{ top: 4, right: 8, left: -10, bottom: 0 }}
              onMouseMove={trackHover}
              onClick={openDraftAt}
              style={{ cursor: 'crosshair' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" className="stroke-gray-200" />
              <XAxis dataKey="t" tick={{ fontSize: 11, fill: '#6b7280', className: 'fill-gray-500' }} interval={xAxisInterval} minTickGap={24} />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280', className: 'fill-gray-500' }} domain={[0, 100]} unit="%" />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area type="monotone" dataKey="cpu" name={t('infra.hostCPU')} stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.2} strokeWidth={2} />
              <Area type="monotone" dataKey="mem_pct" name={t('infra.hostRAM')} stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={2} />
              {annotationsForChart.map(({ a, t: tickLabel }) => (
                <ReferenceLine
                  key={a.id}
                  x={tickLabel}
                  stroke="#a855f7"
                  strokeDasharray="3 3"
                  strokeWidth={1.5}
                  ifOverflow="extendDomain"
                />
              ))}
              <Brush
                dataKey="t"
                height={22}
                stroke="#9ca3af"
                fill="#f9fafb"
                travellerWidth={8}
                tickFormatter={() => ''}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState className="py-10" title={t('infra.noHostData')} />
        )}

        {/* Annotation list — click to delete; hover shows author + time. */}
        {annotationsForChart.length > 0 && (
          <ul className="mt-4 flex flex-wrap gap-2">
            {annotationsForChart.map(({ a }) => (
              <li
                key={a.id}
                className="group inline-flex items-center gap-2 bg-purple-50 border border-purple-200 text-purple-900 rounded-full px-3 py-1 text-xs"
                title={`${new Date(a.ts).toLocaleString()} · ${a.author_email}`}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                <span className="max-w-[260px] truncate">{a.body}</span>
                <LinkButton
                  tone="danger"
                  onClick={() => removeAnnotation(a.id)}
                  disabled={deleteAnnotation.isPending}
                  className="opacity-40 hover:opacity-100"
                  aria-label={t('infra.annotationDelete')}
                >
                  ×
                </LinkButton>
              </li>
            ))}
          </ul>
        )}

        {annotError && <p className="mt-3 text-xs text-red-600">{annotError}</p>}

        {/* Draft popover — renders inline below the chart so positioning isn't a battle. */}
        {draftAnnot && (
          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-3">
            <Field
              label={t('infra.annotationAt', { time: draftAnnot.bucketLabel })}
              hint={`${draftBody.length}/${ANNOTATION_MAX}`}
            >
              {(props) => (
                <Textarea
                  {...props}
                  autoFocus
                  value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                  maxLength={ANNOTATION_MAX}
                  rows={2}
                  placeholder={t('infra.annotationPlaceholder')}
                />
              )}
            </Field>
            <div className="flex items-center justify-end gap-2 mt-2">
              <Button variant="ghost" size="sm" onClick={() => setDraftAnnot(null)}>
                {t('infra.annotationCancel')}
              </Button>
              <Button
                size="sm"
                onClick={submitAnnotation}
                loading={createAnnotation.isPending}
                disabled={!draftBody.trim()}
              >
                {t('infra.annotationSave')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Per-instance table */}
      <Card>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h2 className="text-sm font-medium text-gray-700">{t('infra.perInstance')}</h2>
          {data && (
            <p className="text-xs text-gray-500">
              {t('infra.costSplit', {
                customer: fmtTRY(data.aggregate.cost_customer_try),
                platform: fmtTRY(data.aggregate.cost_platform_try),
              })}
            </p>
          )}
        </div>
        {isLoading ? (
          <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : sortedRows.length === 0 ? (
          <EmptyState className="py-8" title={t('infra.noInstances')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                  <SortableTh label={t('infra.colInstance')} sortKey="subdomain" current={sortKey} dir={sortDir} onSort={toggleSort} className="pb-2" />
                  <th className="pb-2 font-medium">{t('infra.colOwner')}</th>
                  <th className="pb-2 font-medium">{t('infra.colStatus')}</th>
                  <SortableTh label={t('infra.colCPU')} sortKey="cpu_percent" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <SortableTh label={t('infra.colMem')} sortKey="mem_bytes" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <th className="pb-2 font-medium text-right">{t('infra.colAllocated')}</th>
                  <SortableTh label={t('infra.colStorage')} sortKey="storage_bytes" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <SortableTh label={t('infra.colNet30d')} sortKey="net_30d_bytes" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <SortableTh label={t('infra.colUptime')} sortKey="uptime_percent" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                  <SortableTh label={t('infra.colCost')} sortKey="cost_try_30d" current={sortKey} dir={sortDir} onSort={toggleSort} align="right" className="pb-2 text-right" />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row: InfraInstanceRow) => (
                  <tr key={row.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-2.5 pr-3">
                      <Link to={`/instances/${row.id}/resources`} className="font-medium text-brand-600 hover:text-brand-700">{row.subdomain}</Link>
                      <div className="text-xs text-gray-400 truncate max-w-[180px]">{row.name}</div>
                      {row.cost_bucket === 'platform' && (
                        <Badge tone="neutral" className="mt-0.5">{t('infra.platformBucket')}</Badge>
                      )}
                    </td>
                    <td className="py-2.5 text-gray-600 truncate max-w-[180px]">{row.owner_email || '-'}</td>
                    <td className="py-2.5"><StatusPill status={row.status} /></td>
                    <td className="py-2.5 text-right text-gray-700">{row.status === 'running' ? fmtPct(row.cpu_percent) : '-'}</td>
                    <td className="py-2.5 text-right text-gray-700">{row.status === 'running' ? fmtBytes(row.mem_bytes) : '-'}</td>
                    <td className="py-2.5 text-right text-gray-600">{fmtFixed(row.allocated_cores, 1)}c · {fmtFixed(row.allocated_ram_gb, 1)}GB</td>
                    <td className="py-2.5 text-right text-gray-700">{fmtBytes(row.storage_bytes)}</td>
                    <td className="py-2.5 text-right text-gray-600">{fmtBytes(row.net_30d_bytes)}</td>
                    <td className="py-2.5 text-right text-gray-600">{fmtPct(row.uptime_percent, 0)}</td>
                    <td className="py-2.5 text-right font-medium text-gray-900">{fmtTRY(row.cost_try_30d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Top 5 lists side by side */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <TopList title={t('infra.topCPU')} rows={data.top_cpu.slice(0, 5)} valueLabel={(r) => fmtPct(r.cpu_percent)} />
          <TopList title={t('infra.topStorage')} rows={data.top_storage.slice(0, 5)} valueLabel={(r) => fmtBytes(r.storage_bytes)} />
        </div>
      )}

      {/* Rates footer */}
      {data && (
        <div className="mt-8 text-xs text-gray-400">
          <p>
            {t('infra.costModelHint', { basis: fmtTRY(data.rates?.cost_basis) })}
          </p>
        </div>
      )}
    </div>
  )
}

function TopList({ title, rows, valueLabel }: { title: string; rows: InfraInstanceRow[]; valueLabel: (r: InfraInstanceRow) => string }) {
  return (
    <Card>
      <h3 className="text-sm font-medium text-gray-700 mb-3">{title}</h3>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center justify-between text-sm">
            <Link to={`/instances/${r.id}/resources`} className="text-brand-600 hover:text-brand-700 truncate max-w-[60%]">{r.subdomain}</Link>
            <span className="text-gray-700 font-medium">{valueLabel(r)}</span>
          </li>
        ))}
      </ul>
    </Card>
  )
}
